import {
  Box, BoxProps, ButtonGroup, HStack, IconButton, Skeleton, Text, Popover, PopoverTrigger, PopoverContent, PopoverBody, PopoverArrow, VStack, useDisclosure,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { Table } from "@tanstack/react-table";
import { range } from "lodash";
import TableFilterSelect from "components/Select/TableFilterSelect";

interface SortOption {
  label: string;
  value: string;
}

interface PaginationProps<T> extends BoxProps {
  pageSizeOptions?: number[];
  table: Table<T>;
  isLoading?: boolean;
  sortOptions?: SortOption[];
  currentSort?: string;
  defaultSortString?: string;
  onSortChange?: (value: string) => void;
  placement?: "top" | "bottom";
  minItemsForSort?: number;
}

export default function SmallArrowPagination<T>({
  table,
  isLoading = false,
  pageSizeOptions = range(20, 100, 20),
  sortOptions,
  currentSort,
  defaultSortString,
  onSortChange,
  placement = "top",
  minItemsForSort = 2,
  ...boxProps
}: PaginationProps<T>) {
  const effectiveCurrentSort = currentSort || defaultSortString;
  const totalItems = table.getFilteredRowModel().rows.length;
  const hasMultiplePages = table.getPageCount() > 1;
  const shouldShowPageSize = pageSizeOptions && pageSizeOptions.length > 0 && totalItems > Math.min(...pageSizeOptions);
  const shouldShowPagination = hasMultiplePages || shouldShowPageSize;
  const shouldShowSort = placement === "top" && sortOptions && sortOptions.length > 0 && onSortChange && totalItems >= minItemsForSort;
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <Box
      px={{ base: "4", md: "6" }}
      pb={shouldShowPagination ? "5" : "0"}
      {...boxProps}
    >
      <Skeleton isLoaded={!isLoading}>
        <HStack spacing="3" justify="end">
          {shouldShowSort && (
            <TableFilterSelect
              placeholder="Sort By"
              value={sortOptions.find((opt) => opt.value === effectiveCurrentSort) || null}
              onChange={(option) => {
                if (option && !Array.isArray(option)) {
                  onSortChange(option.value);
                } else {
                  onSortChange("");
                }
              }}
              loadOptions={async (inputValue: string) => {
                if (!inputValue) return sortOptions;
                return sortOptions.filter((opt) => opt.label.toLowerCase().includes(inputValue.toLowerCase()));
              }}
              defaultOptions={sortOptions}
              minWidth="220px"
              isClearable={!defaultSortString}
            />
          )}
          {shouldShowPagination && (
            <>
              {shouldShowPageSize ? (
                <Popover placement="bottom-end" isOpen={isOpen} onOpen={onOpen} onClose={onClose}>
                  <PopoverTrigger>
                    <Box>
                      <Text
                        color="fg.muted"
                        textStyle="sm"
                        mr={4}
                        cursor="pointer"
                        userSelect="none"
                        _hover={{ color: "fg.default", textDecoration: "underline" }}
                        onClick={onOpen}
                      >
                        Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                      </Text>
                    </Box>
                  </PopoverTrigger>
                  <PopoverContent width="auto" minWidth="150px">
                    <PopoverArrow />
                    <PopoverBody p={3}>
                      <VStack spacing={2} align="stretch">
                        {pageSizeOptions.map((size) => (
                          <Text
                            key={size}
                            px={3}
                            py={2}
                            cursor="pointer"
                            borderRadius="md"
                            fontSize="sm"
                            fontWeight={table.getState().pagination.pageSize === size ? "semibold" : "normal"}
                            bg={table.getState().pagination.pageSize === size ? "caminoIndigo.50" : "transparent"}
                            color={table.getState().pagination.pageSize === size ? "caminoIndigo.700" : "inherit"}
                            _hover={{ bg: "gray.100" }}
                            onClick={() => {
                              table.setPageSize(size);
                              onClose();
                            }}
                          >
                            {size} per page
                          </Text>
                        ))}
                      </VStack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              ) : (
                <Text color="fg.muted" textStyle="sm" mr={4}>
                  Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
                </Text>
              )}
              {hasMultiplePages && (
                <ButtonGroup
                  spacing="3"
                  justifyContent="space-between"
                  width={{ base: "full", md: "auto" }}
                >
                  <IconButton
                    aria-label="Previous Page"
                    icon={<ChevronLeftIcon boxSize={5} />}
                    onClick={table.previousPage}
                    isDisabled={!table.getCanPreviousPage()}
                    variant="ghost"
                  />
                  <IconButton
                    aria-label="Next Page"
                    icon={<ChevronRightIcon boxSize={5} />}
                    onClick={table.nextPage}
                    isDisabled={!table.getCanNextPage()}
                    variant="ghost"
                  />
                </ButtonGroup>
              )}
            </>
          )}
        </HStack>
      </Skeleton>
    </Box>
  );
}
